import { LoginPagePreview } from '@/components/onboarding/LoginPagePreview'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

type Mode = 'signin' | 'signup'

function PageHeader() {
  return (
    <header className="sticky top-0 z-10 border-b border-border bg-background/80 backdrop-blur">
      <div className="flex items-center h-14 px-6">
        <a href="/" className="font-semibold text-sm tracking-tight">
          RoleRound
        </a>
      </div>
    </header>
  )
}

export default function LoginPage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)

  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [signupSuccess, setSignupSuccess] = useState(false)

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true })
  }, [user, navigate])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      if (mode === 'signin') {
        const { error: authError } = await supabase.auth.signInWithPassword({ email, password })
        if (authError) {
          setError(mapAuthError(authError.message))
        }
        // navigation handled by useEffect on user change
      } else {
        const { error: authError } = await supabase.auth.signUp({ email, password })
        if (authError) {
          setError(mapAuthError(authError.message))
        } else {
          setSignupSuccess(true)
        }
      }
    } finally {
      setLoading(false)
    }
  }

  async function handleGoogleSignIn() {
    setError(null)
    setLoading(true)
    const { error: authError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/dashboard` },
    })
    if (authError) {
      setError(mapAuthError(authError.message))
      setLoading(false)
    }
    // on success, browser redirects — no further action needed
  }

  if (signupSuccess) {
    return (
      <>
        <PageHeader />
        <main className="flex min-h-[calc(100vh-3.5rem)] flex-col lg:flex-row">
          <div className="hidden lg:flex lg:w-1/2 lg:max-w-xl">
            <LoginPagePreview />
          </div>
          <div className="flex flex-1 flex-col items-center justify-center p-8">
            <div className="w-full max-w-sm text-center space-y-4">
              <div className="text-4xl">📬</div>
              <h1 className="text-2xl font-semibold">Check your email</h1>
              <p className="text-muted-foreground text-sm">
                We sent a confirmation link to <strong>{email}</strong>. Click it to activate your
                account.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSignupSuccess(false)
                  setMode('signin')
                }}
                className="text-sm text-primary underline underline-offset-4 hover:opacity-80"
              >
                Back to sign in
              </button>
            </div>
          </div>
        </main>
      </>
    )
  }

  return (
    <>
      <PageHeader />
      <main className="flex min-h-[calc(100vh-3.5rem)] flex-col lg:flex-row">
        {/* Left column — sample feedback preview, desktop only */}
        <div className="hidden lg:flex lg:w-1/2 lg:max-w-xl">
          <LoginPagePreview />
        </div>

        {/* Right column — auth form, always visible */}
        <div className="flex flex-1 flex-col items-center justify-center p-8">
          <div className="w-full max-w-sm space-y-6">
            <p className="text-center text-sm text-muted-foreground">
              {mode === 'signin' ? 'Sign in to continue practicing' : 'Create your free account'}
            </p>

            {/* Mode toggle */}
            <div className="flex rounded-lg border border-border overflow-hidden text-sm font-medium">
              {(['signin', 'signup'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    setMode(m)
                    setError(null)
                  }}
                  className={cn(
                    'flex-1 py-2 transition-colors',
                    mode === m
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:text-foreground'
                  )}
                >
                  {m === 'signin' ? 'Sign in' : 'Sign up'}
                </button>
              ))}
            </div>

            {/* Google OAuth */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 rounded-lg border border-border bg-background py-2.5 text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
            >
              <GoogleIcon />
              Continue with Google
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <div className="flex-1 border-t border-border" />
              or
              <div className="flex-1 border-t border-border" />
            </div>

            {/* Email / password form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="email" className="text-sm font-medium">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="you@example.com"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="text-sm font-medium">
                    Password
                  </label>
                  {mode === 'signin' && (
                    <a
                      href="/forgot-password"
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Forgot password?
                    </a>
                  )}
                </div>
                <input
                  id="password"
                  type="password"
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder={mode === 'signup' ? 'At least 8 characters' : ''}
                />
              </div>

              {error && (
                <p
                  role="alert"
                  className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {loading ? 'Please wait…' : mode === 'signin' ? 'Sign in' : 'Create account'}
              </button>
            </form>

            {mode === 'signin' && (
              <p className="text-center text-xs text-muted-foreground">
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setMode('signup')
                    setError(null)
                  }}
                  className="text-primary underline underline-offset-4 hover:opacity-80"
                >
                  Sign up free
                </button>
              </p>
            )}
          </div>
        </div>
      </main>
    </>
  )
}

function GoogleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <title>Google</title>
      <path
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  )
}

function mapAuthError(message: string): string {
  if (message.includes('Invalid login credentials')) return 'Incorrect email or password.'
  if (message.includes('Email not confirmed')) return 'Please confirm your email before signing in.'
  if (message.includes('User already registered'))
    return 'An account with this email already exists. Try signing in.'
  if (message.includes('Password should be')) return 'Password must be at least 8 characters.'
  if (message.includes('rate limit'))
    return 'Too many attempts. Please wait a moment and try again.'
  return 'Something went wrong. Please try again.'
}
